import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, ClipboardText, Buildings, Leaf, MedalMilitary,
  FirstAid, Factory, Bank, GraduationCap, Truck, Storefront, CaretLeft, CaretRight, Quotes, X,
  Phone, EnvelopeSimple, MapPin,
} from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import FeedbackForm from "@/components/FeedbackForm";
import QuoteForm from "@/components/QuoteForm";
import ContactForm from "@/components/ContactForm";
import { SITE, SERVICES, INDUSTRIES, CERTIFICATIONS, CASE_STUDIES, HERO_METRICS, TESTIMONIALS, WHY_EMPIRE, TRUSTED_BY } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80";

const VALUE_PROPS = [
  { icon: ShieldCheck, title: "Compliance by Design", body: "SAM.gov registered, ISO 9001 systems, and a QASP paper trail your contracting officer will actually approve." },
  { icon: ClipboardText, title: "Operational Excellence", body: "Bar-coded inspections, escalation matrices, and monthly KPI reviews delivered with every account." },
  { icon: Leaf, title: "Safety-Driven Culture", body: "Zero-incident goal. OSHA 30-trained supervisors. Bloodborne pathogens, HIPAA, and HazCom programs." },
];

const INDUSTRY_ICONS = { healthcare: FirstAid, industrial: Factory, government: Bank, office: Buildings, education: GraduationCap, distribution: Truck, municipal: Storefront };

export default function Home() {
  return (
    <>
      <SEO title="Mission-Critical Facility Maintenance" description="Empire Serv, Inc. — mission-critical facility maintenance built on trust, safety, and performance. Healthcare, industrial, and government cleaning and preventative maintenance." path="/" />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center es-grain overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Facility maintenance" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--es-navy-deep)]/98 via-[var(--es-navy)]/92 to-[var(--es-navy)]/75" />
        </div>
        <div className="es-container relative z-10 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-6 es-fade-up">
              <span className="h-px w-10 bg-[var(--es-accent)]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[var(--es-accent)] font-semibold">Healthcare · Industrial · Government</span>
            </div>
            <h1 className="es-h1 text-white es-fade-up" style={{ animationDelay: "0.05s" }}>
              Mission-critical facility maintenance built on <span className="text-[var(--es-accent)]">trust, safety, and performance</span>.
            </h1>
            <p className="mt-6 max-w-xl text-white/85 text-lg leading-relaxed es-fade-up" style={{ animationDelay: "0.15s" }}>
              Empire Serv protects the operations behind hospitals, plants, and federal facilities — with compliant, safety-driven programs and a self-performing workforce that shows up ready.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3 es-fade-up" style={{ animationDelay: "0.25s" }}>
              <Link to="/quote" className="es-btn-accent" data-testid="hero-quote-cta">Request a Quote <ArrowRight size={18} /></Link>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white hover:bg-white hover:text-[var(--es-navy)] px-6 py-3 font-semibold rounded-sm transition-all" data-testid="hero-services-cta">
                Our Services
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block es-fade-up" style={{ animationDelay: "0.4s" }}>
            <div className="bg-[var(--es-navy-deep)] border-l-4 border-[var(--es-accent)] p-7 rounded-sm shadow-2xl">
              <div className="text-xs uppercase tracking-[0.25em] text-[var(--es-accent)] font-semibold mb-4">Credentials</div>
              <ul className="space-y-3 text-white text-sm">
                <li className="flex items-center gap-2.5"><MedalMilitary size={18} className="text-[var(--es-accent)] flex-shrink-0" weight="fill" />ISO 9001:2015 Quality Management</li>
                <li className="flex items-center gap-2.5"><MedalMilitary size={18} className="text-[var(--es-accent)] flex-shrink-0" weight="fill" />CIMS-GB Certified w/ Honors</li>
                <li className="flex items-center gap-2.5"><MedalMilitary size={18} className="text-[var(--es-accent)] flex-shrink-0" weight="fill" />OSHA 30 · GBAC STAR™</li>
                <li className="flex items-center gap-2.5"><MedalMilitary size={18} className="text-[var(--es-accent)] flex-shrink-0" weight="fill" />Active in SAM.gov · NAICS 561720</li>
              </ul>
            </div>
          </div>
        </div>
        {/* Metrics strip */}
        <div className="absolute bottom-0 inset-x-0 bg-white border-t-4 border-[var(--es-accent)]" data-testid="hero-metrics-strip">
          <div className="es-container grid grid-cols-2 md:grid-cols-4">
            {HERO_METRICS.map((s, i) => (
              <div key={s.label} className={`py-6 px-2 text-center ${i !== 0 ? "md:border-l border-[var(--es-border)]" : ""}`}>
                <div className="text-3xl md:text-4xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{s.value}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.18em] text-[var(--es-text-muted)] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="bg-white border-b border-[var(--es-border)] py-14" data-testid="brand-statement-section">
        <div className="es-container max-w-4xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--es-accent)] font-semibold mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-[var(--es-accent)]" />Our Brand Promise
          </div>
          <p className="text-xl md:text-2xl leading-relaxed text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 500 }}>
            {SITE.brand_statement}
          </p>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="bg-[var(--es-bg)] py-12" data-testid="trusted-by-section">
        <div className="es-container">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--es-text-muted)] font-semibold text-center mb-8">Trusted across regulated industries</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
            {TRUSTED_BY.map((t) => (
              <div key={t} className="text-center text-[var(--es-text-muted)] text-sm font-semibold tracking-wide border-l-2 border-[var(--es-accent)]/40 md:border-l-0 md:border-t-2 pt-4 pl-4 md:pl-0" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="es-section bg-white" data-testid="value-props-section">
        <div className="es-container">
          <div className="max-w-2xl">
            <div className="es-eyebrow">Why Empire Serv</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">Three commitments behind every contract.</h2>
            <p className="es-lede mt-4">A self-performing workforce paired with documented systems — so the same standard shows up on day 1, day 100, and during your next audit.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {VALUE_PROPS.map((v, idx) => (
              <div key={v.title} data-testid={`value-prop-${idx}`} className="es-card p-8">
                <div className="w-12 h-12 rounded-sm bg-[var(--es-navy)] text-[var(--es-accent)] flex items-center justify-center mb-5">
                  <v.icon size={22} weight="duotone" />
                </div>
                <h3 className="text-xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{v.title}</h3>
                <p className="mt-3 text-[var(--es-text-muted)] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="es-section bg-[var(--es-bg)]" data-testid="services-overview-section">
        <div className="es-container">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <div className="es-eyebrow">Core Services</div>
              <h2 className="es-h2 text-[var(--es-navy)] mt-3">A full-spectrum facility services partner.</h2>
            </div>
            <Link to="/services" className="es-link-underline">View all services <ArrowRight size={16} /></Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.slice(0, 9).map((s) => (
              <Link to="/services" key={s.slug} data-testid={`home-service-card-${s.slug}`} className="es-card p-7 block group">
                <div className="w-11 h-11 bg-[var(--es-navy)]/5 text-[var(--es-navy)] rounded-sm flex items-center justify-center mb-4 border border-[var(--es-navy)]/10">
                  <MedalMilitary size={20} weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-[var(--es-text-muted)] mt-2 leading-relaxed">{s.blurb}</p>
                <div className="mt-4 flex items-center gap-1 text-[var(--es-navy)] text-sm font-semibold group-hover:gap-2 transition-all">
                  Learn more <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="es-section bg-white" data-testid="industries-strip-section">
        <div className="es-container">
          <div className="es-eyebrow">Industries We Serve</div>
          <h2 className="es-h2 text-[var(--es-navy)] mt-3 max-w-3xl">Built for facilities where clean is non-negotiable.</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((ind) => {
              const Icon = INDUSTRY_ICONS[ind.slug] || Buildings;
              return (
                <Link to="/industries" key={ind.slug} data-testid={`home-industry-${ind.slug}`} className="relative overflow-hidden rounded-sm border border-[var(--es-border)] group h-52">
                  <img src={ind.image} alt={ind.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--es-navy-deep)] via-[var(--es-navy)]/40 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end">
                    <Icon size={20} weight="duotone" className="text-[var(--es-accent)]" />
                    <h3 className="mt-2 text-white text-base font-bold" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{ind.name}</h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY EMPIRE COMPARISON */}
      <section className="es-section bg-[var(--es-navy-deep)] text-white relative es-grain overflow-hidden" data-testid="why-empire-section">
        <div className="es-container relative">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--es-accent)] font-semibold">The Empire Difference</div>
            <h2 className="es-h2 text-white mt-3">Why leading facilities choose Empire Serv.</h2>
            <p className="mt-4 text-white/70 max-w-2xl">A side-by-side of how our program compares to typical janitorial vendors.</p>
          </div>
          <div className="mt-12 border border-white/10 rounded-sm overflow-hidden">
            <div className="grid grid-cols-3 bg-white/5 border-b border-white/10 text-xs uppercase tracking-[0.2em] font-semibold">
              <div className="p-4 md:p-5 text-white/60">Attribute</div>
              <div className="p-4 md:p-5 text-[var(--es-accent)] flex items-center gap-2"><ShieldCheck size={16} weight="fill" />Empire Serv</div>
              <div className="p-4 md:p-5 text-white/40 flex items-center gap-2"><X size={16} weight="bold" />Typical Vendor</div>
            </div>
            {WHY_EMPIRE.map((row, i) => (
              <div key={i} className="grid grid-cols-3 border-b border-white/5 last:border-0 text-sm">
                <div className="p-4 md:p-5 text-white/60 font-semibold hidden md:block">{["Workforce", "Quality System", "Contracting", "Response", "Chemistry", "Accountability"][i]}</div>
                <div className="p-4 md:p-5 text-white col-span-2 md:col-span-1 border-l border-white/5">{row.us}</div>
                <div className="p-4 md:p-5 text-white/50 border-l border-white/5 hidden md:block">{row.them}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDY SNIPPET */}
      <section className="es-section bg-white" data-testid="home-case-studies-section">
        <div className="es-container">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <div className="es-eyebrow">Proven Performance</div>
              <h2 className="es-h2 text-[var(--es-navy)] mt-3">Outcomes that defend the next option year.</h2>
            </div>
            <Link to="/case-studies" className="es-link-underline">All case studies <ArrowRight size={16} /></Link>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {CASE_STUDIES.slice(0, 3).map((c) => (
              <Link to="/case-studies" key={c.slug} data-testid={`home-case-${c.slug}`} className="es-card overflow-hidden block group">
                <div className="overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--es-accent)]">{c.sector}</div>
                  <h3 className="mt-2 text-lg font-bold text-[var(--es-navy)] leading-snug" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{c.title}</h3>
                  <p className="mt-3 text-sm text-[var(--es-text-muted)] line-clamp-3">{c.result}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* RATE US — native feedback form */}
      <section className="es-section bg-white" data-testid="home-rate-section">
        <div className="es-container">
          <div className="max-w-3xl mb-10">
            <div className="es-eyebrow">Rate Us</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">Your feedback matters.</h2>
            <p className="es-lede mt-4">
              Thank you for choosing Empire Serv. Your feedback helps us recognize outstanding team members, improve our service, and continue delivering the quality and reliability our clients expect.
            </p>
          </div>
          <FeedbackForm />
        </div>
      </section>

      {/* REQUEST A QUOTE — native form */}
      <section className="es-section bg-[var(--es-bg)]" data-testid="home-quote-section">
        <div className="es-container">
          <div className="max-w-3xl mb-10">
            <div className="es-eyebrow">Request a Quote</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">Request Your Free Facility Assessment.</h2>
            <p className="es-lede mt-4">
              Professional cleaning solutions tailored to your business. Tell us about your facility and service needs — an Empire Serv specialist will contact you within one business day.
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>

      {/* CONTACT US — native form */}
      <section className="es-section bg-white" data-testid="home-contact-section">
        <div className="es-container">
          <div className="max-w-3xl mb-10">
            <div className="es-eyebrow">Contact Us</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">Start the conversation.</h2>
            <p className="es-lede mt-4">
              Send us a message and we'll respond within one business day — or reach a real person directly at{" "}
              <a href={SITE.phoneHref} className="text-[var(--es-accent)] font-semibold hover:underline">{SITE.phone}</a>{" "}or{" "}
              <a href={`mailto:${SITE.email}`} className="text-[var(--es-accent)] font-semibold hover:underline">{SITE.email}</a>.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* CERTS BAR */}
      <section className="bg-white border-y border-[var(--es-border)] py-10" data-testid="home-certs-bar">
        <div className="es-container flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {CERTIFICATIONS.map((c) => (
            <div key={c.name} className="text-sm font-semibold text-[var(--es-text-muted)] tracking-wide" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {c.name}
            </div>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}

function Testimonials() {
  const [i, setI] = useState(0);
  const n = TESTIMONIALS.length;
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % n), 7000);
    return () => clearInterval(id);
  }, [n]);
  const t = TESTIMONIALS[i];
  return (
    <section className="es-section bg-[var(--es-bg)]" data-testid="testimonials-section">
      <div className="es-container max-w-5xl">
        <div className="es-eyebrow text-center">What clients say</div>
        <div className="mt-6 relative">
          <Quotes size={64} weight="fill" className="text-[var(--es-accent)]/25 absolute -top-4 left-0" />
          <blockquote key={i} className="es-fade-up text-2xl md:text-3xl leading-snug text-[var(--es-navy)] font-medium pt-8 pl-10 md:pl-16" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            "{t.quote}"
          </blockquote>
          <div className="mt-8 pl-10 md:pl-16">
            <div className="font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{t.name}</div>
            <div className="text-sm text-[var(--es-text-muted)]">{t.role}</div>
          </div>
        </div>
        <div className="mt-10 flex items-center gap-4 justify-center">
          <button data-testid="testimonial-prev" onClick={() => setI((i - 1 + n) % n)} className="w-10 h-10 border border-[var(--es-border)] rounded-sm flex items-center justify-center text-[var(--es-navy)] hover:bg-[var(--es-navy)] hover:text-white transition-colors">
            <CaretLeft size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-2">
            {TESTIMONIALS.map((_, idx) => (
              <button key={idx} data-testid={`testimonial-dot-${idx}`} onClick={() => setI(idx)} className={`h-1.5 transition-all ${idx === i ? "w-8 bg-[var(--es-accent)]" : "w-3 bg-[var(--es-border)]"}`} />
            ))}
          </div>
          <button data-testid="testimonial-next" onClick={() => setI((i + 1) % n)} className="w-10 h-10 border border-[var(--es-border)] rounded-sm flex items-center justify-center text-[var(--es-navy)] hover:bg-[var(--es-navy)] hover:text-white transition-colors">
            <CaretRight size={18} weight="bold" />
          </button>
        </div>
      </div>
    </section>
  );
}
